import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { fonts, colors, links } from '../common/mixins.js';
import Icon from '../common/Icon.jsx';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import useMediaQuery from '@mui/material/useMediaQuery';
import { styled as muiStyled } from '@mui/system';
import { createTheme } from '@mui/material/styles';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Overlay from "../common/Overlay";
import useModal from "../../hooks/useModal";

const StyledMenu = muiStyled(Menu)`
  & .MuiPaper-root {
    border-radius: 8px;
    box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.24);
    margin-top: 8px;
    padding:8px;
    display:flex;
    flex-direction:column;
    gap:8px;
  }
  & .MuiList-root {
    padding-top: 0;
    padding-bottom: 0;
  }
`;

const StyledMenuItem = muiStyled(MenuItem)`
${fonts.darkPurpleText()};
display:flex;
align-items:center;
gap:4px;
min-width:142px;
padding: ${(props) => (props.isMobile ? '8px 8px 8px 0 ' : '8px')};
border-radius:8px;
 &:focus {
    background-color: transparent;
  }
  &:hover {
    background-color: ${(props) => (props.isMobile ? 'transparent' : colors.lightPrimary)};
  }
pointer-events: ${(props) => (props.hasTicketsReceiptPdf ? 'auto' : 'none')};
`;

const StyledLink = styled.a`
  ${links.linkNormalBold()};
  display:flex;
  gap:4px;
  font-size:14px;
  align-items:center;
  line-height: 24px;
  cursor: pointer;
   &:hover {       
      text-decoration: none;
    i {
        background-image: url('/Site/images/icons/order-details/download-hover.svg');
      }
    }
`;

const StyledLinkUnclickable = styled.div`
  ${fonts.grayTextBold()};
  text-decoration: none;
  display:flex;
  gap:4px;
  font-size:14px;
  align-items:center;
  line-height: 24px;
`;

const Container = styled.div`
width:100%;
margin-top:20px;
display:flex;
justify-content:flex-end;
padding-right:8px;
 @media (max-width: 1000px) {
  justify-content:flex-start;
  padding-right: 0;
  }
`;

const DownloadReceipts = ({ receiptUrls }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const { isOpen, openModal, closeModal } = useModal();
    const isMobile = useMediaQuery('(max-width:767px)');
    const open = Boolean(anchorEl);
    const { serviceFeeReceiptUrl, donationFeeReceiptUrl, ticketsReceiptUrl, hasTicketsReceiptPdf, hasServiceFeeReceiptPdf } = receiptUrls;
    const hasMultipleReceipts = [serviceFeeReceiptUrl, donationFeeReceiptUrl, ticketsReceiptUrl].filter(receipt => receipt != null && receipt !== '' && receipt !== undefined).length > 1;
 
    const handleClick = (event) => {
        if (!hasMultipleReceipts) {
            const singleReceiptUrl = serviceFeeReceiptUrl || donationFeeReceiptUrl || ticketsReceiptUrl;
            window.open(singleReceiptUrl, '_blank');
        } else {
            if (isMobile) {
                openModal();
            } else {
                setAnchorEl(event.currentTarget);
            }
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const getDownloadText = (hasMultipleReceipts,serviceFeeReceiptUrl, donationFeeReceiptUrl, ticketsReceiptUrl) => {
        if (!hasMultipleReceipts&&serviceFeeReceiptUrl) {
            return orderdetails_resources.ServiceFeeReceipt; //only service fee
        } else if (!hasMultipleReceipts&&donationFeeReceiptUrl) {
            return orderdetails_resources.AdditionalChargeReceipt; //only donation fee
        } else if (!hasMultipleReceipts && ticketsReceiptUrl) {
            return orderdetails_resources.TicketsPaymentReceipt; // only tickets fee
        }
        return orderdetails_resources.DownloadReceipts;
    };

    const theme = createTheme({
        components: {
            MuiBackdrop: {
                styleOverrides: !isMobile ? {
                    root: {
                        backgroundColor: 'transparent',
                        backdropFilter: 'none',
    
                        '&.MuiBackdrop-invisible': {
                            backgroundColor: 'transparent',
                            backdropFilter: 'none'
                        }
                    }
                } : {}
            }
        }
    });

    const renderMenuItems = () => (
        <>
            {ticketsReceiptUrl && <StyledMenuItem
                id="ticketsReceiptLink"
                href={ticketsReceiptUrl}
                target="_blank"
                component="a"
                isMobile={isMobile}
                hasTicketsReceiptPdf={hasTicketsReceiptPdf}
                onClick={isMobile ? closeModal : handleClose}>
                {hasTicketsReceiptPdf ? <Icon kind="download-black" /> : <Icon kind="spinner" /> }
                {orderdetails_resources.TicketsPaymentReceipt}
            </StyledMenuItem>}
            {serviceFeeReceiptUrl && <StyledMenuItem
                id="serviceFeeReceiptLink"
                href={serviceFeeReceiptUrl}
                target="_blank"
                component="a"
                isMobile={isMobile}
                hasTicketsReceiptPdf={hasServiceFeeReceiptPdf}
                onClick={isMobile ? closeModal : handleClose}>
                {hasServiceFeeReceiptPdf ? <Icon kind="download-black" /> : <Icon kind="spinner" />}
                {orderdetails_resources.ServiceFeeReceipt}
            </StyledMenuItem>}
            {donationFeeReceiptUrl && <StyledMenuItem
                id="donationReceiptLink"
                href={donationFeeReceiptUrl}
                target="_blank"
                component="a"
                isMobile={isMobile}
                hasTicketsReceiptPdf={true}
                onClick={isMobile ? closeModal : handleClose}>
                <Icon kind="download-black" />
                {orderdetails_resources.AdditionalChargeReceipt}
            </StyledMenuItem>}
        </>
    )

    const handleScroll = () => {
        if (open ||isOpen) {
            handleClose();
        }

    };
    const isEmpty = (val) => !val || val.trim() === "";

    const hasAllReceiptsAvailable = (!isEmpty(serviceFeeReceiptUrl) && hasServiceFeeReceiptPdf) || (!isEmpty(ticketsReceiptUrl) && hasTicketsReceiptPdf);
    const orderDoesNotHaveReceipts = (isEmpty(serviceFeeReceiptUrl) && !hasServiceFeeReceiptPdf) && (isEmpty(ticketsReceiptUrl) && !hasTicketsReceiptPdf);

    useEffect(() => {      
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [open, isOpen]);
    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <Container>
                {(hasAllReceiptsAvailable) || (hasMultipleReceipts) ?
                    <StyledLink onClick={handleClick}>
                        <Icon kind="download" />
                        <div id="downloadReceipts"> {getDownloadText(hasMultipleReceipts, serviceFeeReceiptUrl, donationFeeReceiptUrl, ticketsReceiptUrl)}</div>
                    </StyledLink> :
                    (!orderDoesNotHaveReceipts) &&
                    <StyledLinkUnclickable>
                        <Icon kind="spinner" />
                        <div id="downloadReceipts"> {orderdetails_resources.DownloadReceipts}</div>
                     </StyledLinkUnclickable>
                }
                {hasMultipleReceipts && (
                    isMobile ? 
                        <Overlay
                            handleClose={closeModal}
                            open={isOpen}
                            id="order-details-receipt"
                            titleText={getDownloadText(hasMultipleReceipts, serviceFeeReceiptUrl, donationFeeReceiptUrl, ticketsReceiptUrl)}
                        >
                            {renderMenuItems()}
                        </Overlay>
                    :
                        <StyledMenu
                            id="download-receipts-menu"
                            anchorEl={anchorEl}
                            open={open}
                            onClose={handleClose}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                        >
                            {renderMenuItems()}
                        </StyledMenu>
                )}
            </Container>
        </ThemeProvider>
    );
};

export default DownloadReceipts;
